import { AxiosResponse, AxiosError } from 'axios';
import api from '../api';
import { DeviceTypes, GENDER, MODALITIES } from '../constants/common.constant';
import { SORT_ORDER } from '../constants/filter.constant';
import { ORDER_PRIORITY, ORDER_STATUS } from '../constants/order.constant';
import { MATCH_STATUS } from '../constants/study.constant';
import {
  DefaultResponse,
  InstitutionProp,
  Procedure,
  User,
  UserResponse,
} from '../interfaces/index';
import { Institution } from '../interfaces';
import { ResponseGetDevices } from './devices.api';
import { Omit, Pick } from '@vindr/utils/helpers/common.helper';
import { ResponseGetDepartment } from './department.api';
import { DefaultErrorResponse } from '../interfaces';
import { ESIGN_STATUS } from './esign.api';
import { INDICATOR } from './report.api';

const ORDER_URL = '/ris/orders/v1';

export interface DeviceStatistic {
  device_id?: string;
  device_name: string;
  status_count: {
    [key: number]: number;
  };
}

export interface ResponseGetDeviceStatistics extends DefaultResponse {
  data: DeviceStatistic[];
}
export const getDeviceStatistics = async (params: ParamsGetOrders = {}) => {
  const response = (await api({
    url: ORDER_URL + '/device-statistics',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetDeviceStatistics>;

  return response.data;
};

export interface ParamsGetDeviceStatisticsAll {
  institution_ids?: string[];
  modalities?: string[];
  scheduled_date_from: string;
  scheduled_date_to: string;
}

export const getDeviceStatisticsAll = async (
  params: ParamsGetDeviceStatisticsAll
) => {
  const response = (await api({
    url: ORDER_URL + '/device-statistics/all',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetDeviceStatistics>;

  return response.data;
};

export interface ParamsGetOrders {
  patient_arrived_date_from?: string;
  patient_arrived_date_to?: string;
  priority?: ORDER_PRIORITY;
  started_date_from?: string;
  started_date_to?: string;
  suspended_date_from?: string;
  suspended_date_to?: string;
  approved_date_from?: string;
  approved_date_to?: string;
  canceled_date_from?: string;
  canceled_date_to?: string;
  completed_date_from?: string;
  completed_date_to?: string;
  delivered?: boolean;
  delivered_date_from?: string;
  delivered_date_to?: string;
  institution_ids?: string[];
  limit?: number;
  match_status?: MATCH_STATUS;
  offset?: number;
  order_group?: string;
  modalities?: string[];
  patient_id?: string;
  patient_name?: string;
  scheduled_date_from?: string;
  scheduled_date_to?: string;
  sort_field?:
    | ''
    | 'order_group'
    | 'patient_id'
    | 'visit_id'
    | 'patient_name'
    | 'scheduled_date'
    | 'patient_arrived_time'
    | 'order_date';
  sort_order?: SORT_ORDER;
  status?: ORDER_STATUS[];
  study_desc?: string;
  study_id?: string;
  visit_id?: string;
}

export interface Order {
  created_at: string;
  approved_at: string;
  approved_by_name: string;
  approved_by_username: string;
  canceled_at: string;
  canceled_by_name: string;
  canceled_by_username: string;
  completed_at: string;
  completed_by_name: string;
  completed_by_username: string;
  delivered: true;
  delivered_at: string;
  delivered_by_name: string;
  delivered_by_username: string;
  order_date: string;
  priority: number;
  received_by_name: string;
  received_by_username: string;
  scheduled_by_name: string;
  scheduled_by_username: string;
  started_at: string;
  started_by_name: string;
  started_by_username: string;
  suspended_at: string;
  suspended_by_name: string;
  suspended_by_username: string;
  device: {
    active: boolean;
    api_key: string;
    code: string;
    created_at: string;
    description: string;
    hostname: string;
    id: string;
    institution_id: string;
    modality: MODALITIES;
    name: string;
    port: number | null;
    sftp_public_key: string;
    sftp_user: string;
    source_aetitle: string;
    type: DeviceTypes;
    updated_at: string;
  };
  device_id: string;
  diagnostic: string;
  id: string;
  institution: InstitutionProp;
  institution_id: string;
  metadata: null | unknown;
  order_doctor_name: string;
  order_group: string;
  patient_arrived_time: string;
  procedure: Procedure;
  procedure_id: string;
  refer_doctor_name: string;
  scheduled_date: string;
  source: number;
  status: number;
  study: {
    comment: string;
    department: string;
    desc: string;
    device_id: string;
    id: string;
    patient_age: string;
    // ref_pacs_id: string; // Not use on FE
    study_date: string;
    study_instance_uid: string;
  };
  study_id: string;
  updated_at: string;
  user: UserResponse;
  user_id: string;
  visited_patient: {
    address: string;
    birth_date: string;
    email: string;
    name: string;
    patient_id: string;
    phone: string;
    sex: GENDER;
    visit_id: string;
  };
  visited_patient_id: string;
}

export interface NewOrder {
  accession_no: string;
  approved_at: null | string;
  approved_by_name: string;
  approved_by_username: string;
  cancelled_at: null | string;
  cancelled_by_name: string;
  cancelled_by_username: string;
  completed_at: null | string;
  completed_by_name: string;
  completed_by_username: string;
  created_at: string;
  delivered: boolean;
  delivered_at: null | string;
  delivered_by_name: string;
  delivered_by_username: string;
  department: ResponseGetDepartment['data'][0];
  department_id: string;
  device: ResponseGetDevices['data'][0];
  device_id: string;
  diagnostic: string;
  esign_status: ESIGN_STATUS;
  his_vendor: string | null;
  id: string;
  institution: Institution;
  institution_id: string;
  metadata: null | { [key in string]: any };
  order_date: string;
  order_doctor_name: string;
  order_group: string;
  ordinal_number: number;
  patient_arrived_time: null | string;
  priority: number;
  procedure: Procedure;
  procedure_id: string;
  received_by_name: string;
  received_by_username: string;
  refer_doctor_name: string;
  request_department: string;
  result_delivery_location: string;
  scheduled_by_name: string;
  scheduled_by_username: string;
  scheduled_date: string;
  source: number;
  started_at: null;
  started_by_name: string;
  started_by_username: string;
  status: ORDER_STATUS;
  study: {
    comment: string;
    department: string;
    desc: string;
    device_id: string;
    id: string;
    patient_age: string;
    // ref_pacs_id: string; // Not use on FE
    study_date: string;
    study_instance_uid: string;
  };
  study_id: string;
  suspended_at: null;
  suspended_by_name: string;
  suspended_by_username: string;
  tech_notes:
    | null
    | {
        text: string;
        time: string;
        user: string;
      }[];
  updated_at: string;
  user: User;
  user_id: string;
  visited_patient: VisitedPatient;
  visited_patient_id: string;
}

export interface ResponseGetOrders extends DefaultResponse {
  data: NewOrder[];
}
export const getOrders = async (
  params: ParamsGetOrders = {
    offset: 0,
    limit: 100,
  }
) => {
  try {
    const response = (await api({
      url: ORDER_URL,
      method: 'GET',
      params,
      timeout: 30_000,
    })) as AxiosResponse<ResponseGetOrders>;

    return response.data;
  } catch (error) {
    throw error as AxiosError<DefaultErrorResponse>;
  }
};

export interface ParamsGetOrderWorklist {
  assign_to?: string;
  esign_status?: ESIGN_STATUS;
  filter_id?: string;
  institution_ids?: string[];
  latest_indicator?: INDICATOR;
  limit?: number;
  match_status?: MATCH_STATUS;
  modalities?: MODALITIES[];
  offset?: number;
  order_date_from?: string;
  order_date_to?: string;
  order_group?: string;
  patient_arrived_time_from?: string;
  patient_arrived_time_to?: string;
  patient_id?: string;
  patient_name?: string;
  sort_field?:
    | 'order_date'
    | 'patient_arrived_time'
    | 'started_at'
    | 'completed_at'
    | 'approved_at'
    | 'delivered_at'
    | 'scheduled_date'
    | 'suspended_at'
    | 'cancelled_at'
    | 'priority'
    | 'study_date'
    | 'patient_birth_date';
  sort_order?: SORT_ORDER;
  statuses?: ORDER_STATUS[];
  study_desc?: string;
  study_instance_uid?: string;
  visit_id?: string;
}

export interface OrderWorklist {
  accession_no: string;
  approved_at: string | null;
  approved_by_name: string;
  approved_by_username: string;
  assignee?: {
    full_name: string;
    id: string;
    username: string;
  };
  cancelled_at: string | null;
  cancelled_by_name: string;
  cancelled_by_username: string;
  completed_at: string | null;
  completed_by_name: string;
  completed_by_username: string;
  delivered_at: string | null;
  delivered_by_name: string;
  delivered_by_username: string;
  device_name: string;
  diagnostic: string;
  esign_status: ESIGN_STATUS;
  gender: GENDER;
  id: string;
  institution: Institution;
  institution_id: string;
  latest_indicator: INDICATOR;
  match_status: number;
  modality: MODALITIES;
  order_date: string | null;
  order_doctor_name: string;
  order_group: string;
  patient_arrived_time: string | null;
  patient_birth_date: string;
  patient_id: string;
  patient_name: string;
  patient_phone: string;
  priority: number;
  received_by_name: string;
  received_by_username: string;
  refer_doctor_name: string;
  request_department: string;
  result_delivery_location: string;
  scheduled_by_name: string;
  scheduled_by_username: string;
  scheduled_date: string;
  started_at: string | null;
  started_by_name: string;
  started_by_username: string;
  status: ORDER_STATUS;
  study_date: string;
  study_desc: string;
  study_id: string;
  study_instance_uid: string;
  suspended_at: string | null;
  suspended_by_name: string;
  suspended_by_username: string;
  tech_notes: string[] | null;
  visit_id: string;
}

export interface ResponseGetOrderWorklist extends DefaultResponse {
  data: OrderWorklist[];
}
export const getOrderWorklist = async (params: ParamsGetOrderWorklist = {}) => {
  const response = (await api({
    url: '/ris/orders/v2/worklist',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetOrderWorklist>;

  return response.data;
};

export interface ResponseGetOrderDetail {
  data: Order;
}
export const getOrderDetail = async (id: string) => {
  const response = (await api({
    url: `${ORDER_URL}/${id}`,
    method: 'GET',
  })) as AxiosResponse<ResponseGetOrderDetail>;

  return response.data;
};

export interface VisitedPatient {
  address: string;
  birth_date: string;
  email: string;
  name: string;
  patient_id: string;
  phone: string;
  sex: number;
  visit_id: string;
}
export interface ParamsCreateOrderGroup {
  device_id: string;
  diagnostic?: string;
  institution_id: string;
  order_doctor_name?: string;
  order_group?: string;
  procedure_ids: string[];
  refer_doctor_name?: string;
  scheduled_date: string;
  visited_patient: {
    address?: string;
    birth_date: string;
    email?: string;
    id?: string;
    patient_id?: string;
    patient_name: string;
    phone?: string;
    sex: GENDER;
    visit_id?: string;
  };
}
export const createOrderGroup = async (data: ParamsCreateOrderGroup) => {
  const response = (await api({
    url: ORDER_URL,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsUpdateOrder {
  id: string;
  device_id?: string;
  diagnostic?: string;
  order_doctor_name?: string;
  department_id?: string;
  procedure_id?: string;
  refer_doctor_name?: string;
  scheduled_date?: string;
  status?: number;
  visited_patient: {
    address?: string;
    birth_date?: string;
    email?: string;
    patient_id?: string;
    patient_name?: string;
    phone?: string;
    sex?: number;
  };
}
export const updateOrder = async (data: ParamsUpdateOrder) => {
  const response = (await api({
    url: ORDER_URL + '/' + data.id,
    method: 'PUT',
    data: JSON.parse(
      JSON.stringify(Omit(data, ['id']), (k, v) => (v === undefined ? null : v))
    ),
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsDeleteOrder {
  id: string;
}
export const deleteOrder = async (data: ParamsDeleteOrder) => {
  const response = (await api({
    url: ORDER_URL + '/' + data.id,
    method: 'DELETE',
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsMatchOrder {
  order_id: string;
  study_id: string;
}
export const matchOrder = async (data: ParamsMatchOrder) => {
  const response = (await api({
    url: ORDER_URL + '/match',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export const unmatchOrder = async (data: ParamsMatchOrder) => {
  const response = (await api({
    url: ORDER_URL + '/unmatch',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsReceptOrder {
  device_id: string;
  institution_id: string;
  order_ids: string[];
  should_merge_order: boolean;
}
export const receptOrder = async (data: ParamsReceptOrder) => {
  const response = (await api({
    url: ORDER_URL + '/recept',
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsChangeOrderDevice {
  device_id: string;
  orders: Order[];
  // should_merge_order: boolean;
}
export const changeOrderDevice = async (data: ParamsChangeOrderDevice) => {
  return await Promise.all(
    data.orders.map((order) =>
      updateOrder({
        ...Pick(order, [
          'approved_at',
          'approved_by_name',
          'approved_by_username',
          'canceled_at',
          'canceled_by_name',
          'canceled_by_username',
          'completed_at',
          'completed_by_name',
          'completed_by_username',
          'delivered',
          'delivered_at',
          'delivered_by_name',
          'delivered_by_username',
          'device_id',
          'diagnostic',
          'order_date',
          'order_doctor_name',
          'patient_arrived_time',
          'priority',
          'procedure_id',
          'received_by_name',
          'received_by_username',
          'refer_doctor_name',
          'scheduled_by_name',
          'scheduled_by_username',
          'scheduled_date',
          'started_at',
          'started_by_name',
          'started_by_username',
          'status',
          'suspended_at',
          'suspended_by_name',
          'suspended_by_username',
        ]),
        id: order.id,
        visited_patient: order.visited_patient && {
          ...Pick(order.visited_patient, [
            'address',
            'birth_date',
            'email',
            'patient_id',
            'phone',
            'sex',
          ]),
          patient_name: order?.visited_patient?.name,
        },
        device_id: data.device_id,
      })
    )
  );
};

export interface ParamsUpdateOrderPriority {
  order_id: string;
  priority: ORDER_PRIORITY;
}
export const updateOrderPriority = async (
  data: ParamsUpdateOrderPriority
): Promise<any> => {
  const response = (await api({
    url: `${ORDER_URL}/priority/${data.order_id}`,
    method: 'PUT',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface OrderNote {
  text: string;
  time: string;
  user: string;
}

export interface ResponseGetOrderNotes extends DefaultResponse {
  data: OrderNote[];
}

export const getOrderNotes = async (id: string) => {
  const response = (await api({
    url: `${ORDER_URL}/notes/${id}`,
    method: 'GET',
  })) as AxiosResponse<ResponseGetOrderNotes>;

  return response.data;
};

export interface ParamsUpdateOrderNote {
  id: string;
  notes: string[];
}

export const updateOrderNote = async (data: ParamsUpdateOrderNote) => {
  const response = (await api({
    url: `${ORDER_URL}/notes/${data.id}`,
    method: 'PUT',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface OrderStatusAudit {
  audit_time: string;
  update_by_name: string;
  update_by_username: string;
}

export interface ParamsChangeStatusOrder {
  notes: string[];
  order_ids: string[];
  order_status_audit: OrderStatusAudit;
  priority: ORDER_PRIORITY;
  status: ORDER_STATUS;
}

export const changeStatusOrder = async (data: ParamsChangeStatusOrder) => {
  const response = (await api({
    url: `${ORDER_URL}/change-status`,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsDeliverOrders {
  delivered_at: string;
  delivered_by_name: string;
  delivered_by_username: string;
  institution_id: string;
  order_ids: string[];
  result_delivery_location: string;
}

export const deliverOrders = async (data: ParamsDeliverOrders) => {
  const response = (await api({
    url: `${ORDER_URL}/deliver`,
    method: 'POST',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};
